// src/components/ListMetricsModal.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import QuickEditModal from './QuickEditModal';

const DEFAULT_TAX_RATE = 9.5;
const DEFAULT_BUDGET = 100;

const ListMetricsModal = ({ 
  isOpen, 
  onClose, 
  onSave,
  items = [],
  initialSelectedMetrics,
  initialTaxRate,
  initialBudget,
  showToast
}) => {
  const { t } = useTranslation();
  const [selectedMetrics, setSelectedMetrics] = useState([]);
  const [taxRate, setTaxRate] = useState(Number.isFinite(initialTaxRate) ? initialTaxRate : DEFAULT_TAX_RATE);
  const [budget, setBudget] = useState(Number.isFinite(initialBudget) ? initialBudget : DEFAULT_BUDGET);
  const [editModal, setEditModal] = useState({ isOpen: false, field: null, currentValue: '' });

  const calculateValues = () => {
    let inListTotal = 0;
    let checkedTotal = 0;
    let uncheckedTotal = 0;

    if (items && items.length > 0) {
      items.forEach(item => {
        const price = parseFloat(item.price) || 0;
        const quantity = parseInt(item.quantity) || 0;
        const total = price * quantity;
        const status = parseInt(item.status);

        inListTotal += total;
        if (status === 1) {
          checkedTotal += total;
        } else {
          uncheckedTotal += total;
        }
      });
    }

    const taxMultiplier = 1 + (taxRate / 100);
    
    const inListPlusTax = inListTotal * taxMultiplier;
    const checkedPlusTax = checkedTotal * taxMultiplier;
    const uncheckedPlusTax = uncheckedTotal * taxMultiplier;
    
    const inListVsBudget = budget - inListPlusTax;
    const checkedVsBudget = budget - checkedPlusTax;
    const uncheckedVsBudget = budget - uncheckedPlusTax;

    return {
      inList: inListTotal,
      inListPlusTax,
      inListVsBudget,
      checked: checkedTotal,
      checkedPlusTax,
      checkedVsBudget,
      unchecked: uncheckedTotal,
      uncheckedPlusTax,
      uncheckedVsBudget
    };
  };

  const values = calculateValues();


  useEffect(() => {
    if (isOpen) {
      setSelectedMetrics(initialSelectedMetrics || []);
      setTaxRate(Number.isFinite(initialTaxRate) ? initialTaxRate : DEFAULT_TAX_RATE);
      setBudget(Number.isFinite(initialBudget) ? initialBudget : DEFAULT_BUDGET);
    }
  }, [isOpen, initialSelectedMetrics, initialTaxRate, initialBudget]);

  const handleMetricToggle = (metricId) => {
    if (selectedMetrics.includes(metricId)) {
      setSelectedMetrics(prev => prev.filter(id => id !== metricId));
    } else {
      if (selectedMetrics.length < 4) {
        setSelectedMetrics(prev => [...prev, metricId]);
      }
    }
  };

  const handleSave = () => {
    localStorage.setItem('list_selected_metrics', JSON.stringify(selectedMetrics));
    localStorage.setItem('list_tax_rate', taxRate.toString());
    localStorage.setItem('list_budget', budget.toString());
    
    if (onSave) {
      onSave({ selectedMetrics, taxRate, budget });
    }
    onClose();
  };

  const openEditModal = (field, currentValue) => {
    setEditModal({
      isOpen: true,
      field,
      currentValue: currentValue.toString()
    });
  };

  const closeEditModal = () => {
    setEditModal({ isOpen: false, field: null, currentValue: '' });
  };

  const handleEditSave = (updateData) => {
    if (editModal.field === 'taxRate') {
      const newValue = parseFloat(updateData.taxRate !== undefined ? updateData.taxRate : updateData.price);
      if (!isNaN(newValue) && newValue >= 0 && newValue <= 100) {
        setTaxRate(newValue);
      }
    } else if (editModal.field === 'budget') {
      const newValue = parseFloat(updateData.budget !== undefined ? updateData.budget : updateData.price);
      if (!isNaN(newValue) && newValue >= 0) {
        setBudget(newValue);
      }
    }
    closeEditModal();
  };

  const formatCurrency = (value) => {
    return `$${Math.abs(value).toFixed(2)}`;
  };

  const getDisplayValue = (value) => {
    if (value < 0) {
      return `-${formatCurrency(value)}`;
    }
    return formatCurrency(value);
  };

  const isMetricSelected = (metricId) => selectedMetrics.includes(metricId);
  const canSelectMore = selectedMetrics.length < 4;

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl">
          {/* Header */}
          <div className="border-b border-gray-200 px-5 py-3">
            <h2 className="text-lg font-bold text-gray-800">{t('metrics.title')}</h2>
          </div>

          {/* Taxes and Budget row */}
          <div className="px-3 py-3 border-b border-gray-200">
            <div className="flex gap-6">
              <div className="flex items-center gap-2">
                <span className="text-sm text-purple-600">{t('metrics.taxes')}</span>
                <span className="text-base font-semibold text-gray-900">{taxRate}%</span>
                <button
                  onClick={() => openEditModal('taxRate', taxRate)}
                  className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-100"
                >
                  <i className="fas fa-pencil-alt text-gray-400 text-xs"></i>
                </button>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-purple-600">{t('metrics.budget')}</span>
                <span className="text-base font-semibold text-gray-900">{formatCurrency(budget)}</span>
                <button
                  onClick={() => openEditModal('budget', budget)}
                  className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-100"
                >
                  <i className="fas fa-pencil-alt text-gray-400 text-xs"></i>
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="py-2">
            {/* Fila 1 - In list */}
            <div className="px-3 py-3 border-b border-gray-200">
              <div className="grid grid-cols-3 gap-6">
                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isMetricSelected('inList')}
                      onChange={() => handleMetricToggle('inList')}
                      disabled={!isMetricSelected('inList') && !canSelectMore}
                      className="w-4 h-4 rounded border-gray-300 text-purple-600"
                    />
                    <span className="text-purple-600 text-sm font-medium">{t('metrics.inList')}</span>
                  </label>
                  <div className="text-base font-bold text-gray-900 mt-0.5 ml-6">
                    {formatCurrency(values.inList)}
                  </div>
                </div>
                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isMetricSelected('inListPlusTax')}
                      onChange={() => handleMetricToggle('inListPlusTax')}
                      disabled={!isMetricSelected('inListPlusTax') && !canSelectMore}
                      className="w-4 h-4 rounded border-gray-300 text-purple-600"
                    />
                    <span className="text-purple-600 text-sm font-medium">{t('metrics.plusTaxes')}</span>
                  </label>
                  <div className="text-base font-bold text-gray-900 mt-0.5 ml-6">
                    {formatCurrency(values.inListPlusTax)}
                  </div>
                </div>
                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isMetricSelected('inListVsBudget')}
                      onChange={() => handleMetricToggle('inListVsBudget')}
                      disabled={!isMetricSelected('inListVsBudget') && !canSelectMore}
                      className="w-4 h-4 rounded border-gray-300 text-purple-600"
                    />
                    <span className="text-purple-600 text-sm font-medium">{t('metrics.vsBudget')}</span>
                  </label>
                  <div className={`text-base font-bold mt-0.5 ml-6 ${values.inListVsBudget < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                    {getDisplayValue(values.inListVsBudget)}
                  </div>
                </div>
              </div>
            </div>

            {/* Fila 2 - Checked */}
            <div className="px-3 py-3 border-b border-gray-200">
              <div className="grid grid-cols-3 gap-6">
                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isMetricSelected('checked')}
                      onChange={() => handleMetricToggle('checked')}
                      disabled={!isMetricSelected('checked') && !canSelectMore}
                      className="w-4 h-4 rounded border-gray-300 text-purple-600"
                    />
                    <span className="text-purple-600 text-sm font-medium">{t('metrics.checked')}</span>
                  </label>
                  <div className="text-base font-bold text-gray-900 mt-0.5 ml-6">
                    {formatCurrency(values.checked)}
                  </div>
                </div>
                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isMetricSelected('checkedPlusTax')}
                      onChange={() => handleMetricToggle('checkedPlusTax')}
                      disabled={!isMetricSelected('checkedPlusTax') && !canSelectMore}
                      className="w-4 h-4 rounded border-gray-300 text-purple-600"
                    />
                    <span className="text-purple-600 text-sm font-medium">{t('metrics.plusTaxes')}</span>
                  </label>
                  <div className="text-base font-bold text-gray-900 mt-0.5 ml-6">
                    {formatCurrency(values.checkedPlusTax)}
                  </div>
                </div>
                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isMetricSelected('checkedVsBudget')}
                      onChange={() => handleMetricToggle('checkedVsBudget')}
                      disabled={!isMetricSelected('checkedVsBudget') && !canSelectMore}
                      className="w-4 h-4 rounded border-gray-300 text-purple-600"
                    />
                    <span className="text-purple-600 text-sm font-medium">{t('metrics.vsBudget')}</span>
                  </label>
                  <div className={`text-base font-bold mt-0.5 ml-6 ${values.checkedVsBudget < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                    {getDisplayValue(values.checkedVsBudget)}
                  </div>
                </div>
              </div>
            </div>

            {/* Fila 3 - Unchecked */}
            <div className="px-3 py-3">
              <div className="grid grid-cols-3 gap-6">
                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isMetricSelected('unchecked')}
                      onChange={() => handleMetricToggle('unchecked')}
                      disabled={!isMetricSelected('unchecked') && !canSelectMore}
                      className="w-4 h-4 rounded border-gray-300 text-purple-600"
                    />
                    <span className="text-purple-600 text-sm font-medium">{t('metrics.unchecked')}</span>
                  </label>
                  <div className="text-base font-bold text-gray-900 mt-0.5 ml-6">
                    {formatCurrency(values.unchecked)}
                  </div>
                </div>
                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isMetricSelected('uncheckedPlusTax')}
                      onChange={() => handleMetricToggle('uncheckedPlusTax')}
                      disabled={!isMetricSelected('uncheckedPlusTax') && !canSelectMore}
                      className="w-4 h-4 rounded border-gray-300 text-purple-600"
                    />
                    <span className="text-purple-600 text-sm font-medium">{t('metrics.plusTaxes')}</span>
                  </label>
                  <div className="text-base font-bold text-gray-900 mt-0.5 ml-6">
                    {formatCurrency(values.uncheckedPlusTax)}
                  </div>
                </div>
                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isMetricSelected('uncheckedVsBudget')}
                      onChange={() => handleMetricToggle('uncheckedVsBudget')}
                      disabled={!isMetricSelected('uncheckedVsBudget') && !canSelectMore}
                      className="w-4 h-4 rounded border-gray-300 text-purple-600"
                    />
                    <span className="text-purple-600 text-sm font-medium">{t('metrics.vsBudget')}</span>
                  </label>
                  <div className={`text-base font-bold mt-0.5 ml-6 ${values.uncheckedVsBudget < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                    {getDisplayValue(values.uncheckedVsBudget)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer buttons */}
          <div className="border-t border-gray-200 px-5 py-3 flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-1.5 text-sm text-gray-600 hover:text-gray-800 font-medium"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-1.5 text-sm bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors"
            >
              {t('common.save')}
            </button>
          </div>
        </div>
      </div>

      <QuickEditModal
        isOpen={editModal.isOpen}
        onClose={closeEditModal}
        field="price"
        context={editModal.field === 'taxRate' ? 'taxRate' : 'budget'}
        currentValue={editModal.currentValue}
        onSaveSuccess={handleEditSave}
        showToast={showToast || (() => {})}
      />
    </>
  );
};

export default ListMetricsModal;